/** "All detected subscriptions" list with flags and usage marks. */

import type { Flag, Subscription, UsageMark } from '../lib/types';
import type { UsageStore } from '../model/usage';
import { sparkline } from './charts';
import { el } from './dom';
import type { Formatters } from './format';

const FLAG_CLASS: Record<Flag['type'], string> = {
  'price-hike': 'flag-hike',
  'renewal-soon': 'flag-renew',
  zombie: 'flag-zombie',
  overlap: 'flag-overlap',
  stopped: 'flag-stopped',
};

const USAGE_OPTIONS: { mark: UsageMark; label: string; title: string }[] = [
  { mark: 'keep', label: 'Use it', title: 'I use this — keep it' },
  { mark: 'rarely', label: 'Rarely', title: 'Rarely use it — count as savable' },
  { mark: 'never', label: 'Never', title: 'Never use it — count as savable' },
];

export function renderSubscriptions(
  container: HTMLElement,
  subs: Subscription[],
  usage: UsageStore,
  fmt: Formatters,
  onMark: (key: string, mark: UsageMark) => void,
): void {
  container.replaceChildren(...subs.map((sub) => row(sub, usage, fmt, onMark)));
}

function row(
  sub: Subscription,
  usage: UsageStore,
  fmt: Formatters,
  onMark: (key: string, mark: UsageMark) => void,
): HTMLElement {
  const mark = usage.get(sub.key);
  const detailText =
    `${sub.cadenceLabel} · ${fmt.money(sub.amount)} · ${sub.chargeCount} charges · ` +
    `${fmt.money(sub.totalSpent)} total · ` +
    (sub.status === 'active' ? `next ~${fmt.date(sub.nextCharge)}` : `last ${fmt.date(sub.lastDate)}`);

  return el(
    'div',
    { class: `sub-row ${sub.status}` },
    el(
      'div',
      { class: 'sub-main' },
      el(
        'div',
        { class: 'sub-name' },
        sub.name,
        ...sub.flags.map((f) =>
          el('span', { class: `flag ${FLAG_CLASS[f.type]}`, title: f.detail }, f.label),
        ),
      ),
      el('div', { class: 'sub-detail' }, el('span', { class: 'chip' }, sub.category), detailText),
    ),
    sparkline(sub),
    el(
      'div',
      { class: 'sub-cost' },
      el('div', { class: 'sub-cost-value' }, fmt.money(sub.monthlyCost), el('span', {}, '/mo')),
      el('div', { class: 'sub-cost-year' }, `${fmt.money(sub.yearlyCost)}/yr`),
    ),
    sub.status === 'active'
      ? el(
          'div',
          { class: 'usage', role: 'radiogroup', aria: { label: `How often do you use ${sub.name}?` } },
          ...USAGE_OPTIONS.map((opt) =>
            el(
              'button',
              {
                class: `usage-btn ${mark === opt.mark ? `on on-${opt.mark}` : ''}`,
                title: opt.title,
                role: 'radio',
                aria: { checked: String(mark === opt.mark) },
                onClick: () => onMark(sub.key, opt.mark),
              },
              opt.label,
            ),
          ),
        )
      : el('div', { class: 'usage usage-inactive' }, 'stopped'),
  );
}
